import React from "react";
import {
    Button,
    ButtonType,
    DateInput,
    Dropdown,
    DropdownOrderBy,
    DropdownRequiredType,
    Form,
    SelectListItem,
    TextInput,
    TwoColumns
} from "@reapptor-apps/reapptor-react-components";
import CruiseWizardPage from "@/pages/Cruise/CruiseWizardPage";
import CruiseArea from "@/models/server/CruiseArea";
import CruiseConstants from "@/helpers/CruiseConstants";
import {Utility} from "@reapptor-apps/reapptor-toolkit";
import WizardContext from "@/pages/Cruise/WizardContext";

import CruiseController from "@/pages/CruiseController";
import EnumProvider from "@/providers/EnumProvider";
import Localizer from "@/localization/Localizer";

import styles from "./Area.module.scss";

import routeImage from "./Images/Route.png";
import routeMobileImage from "./Images/Route_mobile.png";

export interface IAreaProps {
}

interface IAreaState {
    areas: CruiseArea[],
    visitingPointDurationVisible: boolean,
}

const CUSTOM_AREA_ID: string = CruiseConstants.defaultGuid;
const DEPART_BY_MAX_TIME: number = 21;
const ARRIVE_AT_MAX_TIME: number = 23;

export default class Area extends CruiseWizardPage<IAreaProps, IAreaState> {

    state: IAreaState = {
        areas: [],
        visitingPointDurationVisible: false,
    };

    private readonly _departByTimeInputRef: React.RefObject<DateInput> = React.createRef();
    private readonly _arriveAtTimeInputRef: React.RefObject<DateInput> = React.createRef();
    private readonly _formRef: React.RefObject<Form> = React.createRef();

    private async onChangeAsync(): Promise<void> {
        this.saveContext();
        await this.reRenderAsync();
    }

    public async setAreaAsync(value: CruiseArea): Promise<void> {
        const isCustom: boolean = (value.id == CUSTOM_AREA_ID);
        if (isCustom) {
            this.wizard.area = null;
        } else {
            this.wizard.area = value;
            this.wizard.customArea = null;
        }

        await this.onChangeAsync();
    }

    public async setVisitingPointDuration(selectedItem: SelectListItem): Promise<void> {
        this.wizard.visitingPointDuration = (selectedItem != null)
            ? parseInt(selectedItem.value)
            : null;
        
        this.wizard.arriveAt = null;
        
        await this.onChangeAsync();
    }

    public async setDepartByAsync(value: Date): Promise<void> {
        const cruiseDate: Date = new Date(this.wizard.date);

        cruiseDate.setHours(value.getHours(), value.getMinutes());

        this.wizard.departBy = cruiseDate;

        this.wizard.arriveAt = null;

        this.wizard.visitingPointDuration = null;

        await this.onChangeAsync();
    }

    public async setVisitPointAsync(value: string | null): Promise<void> {
        this.wizard.visitPoint = value;

        await this.onChangeAsync();
    }

    public async setDepartFromAsync(value: string | null): Promise<void> {
        this.wizard.departFrom = value;

        await this.onChangeAsync();
    }

    public async setArriveToAsync(value: string | null): Promise<void> {
        this.wizard.arriveTo = value;

        await this.onChangeAsync();
    }

    public async setArriveAtAsync(value: Date): Promise<void> {
        const cruiseDate: Date = new Date(this.wizard.date);

        cruiseDate.setHours(value.getHours(), value.getMinutes());

        this.wizard.arriveAt = cruiseDate;

        WizardContext.setCruiseDuration(this.wizard);

        WizardContext.setCruiseTimeOfDay(this.wizard);

        await this.onChangeAsync();
    }

    public async setCustomAreaAsync(value: string): Promise<void> {
        this.wizard.customArea = value;
        this.saveContext();
    }

    public get isMobile(): boolean {
        return (this.mobile || this.mobileApp);
    }

    public get areas(): CruiseArea[] {
        return this.state.areas;
    }

    public get isCustom(): boolean {
        return (this.areaId == CUSTOM_AREA_ID);
    }

    public get areaId(): string | null {
        return (this.wizard.area) ? this.wizard.area.id : CUSTOM_AREA_ID;
    }

    public get departBy(): Date | null {
        return this.wizard.departBy;
    }

    public get departFrom(): string | null {
        return this.wizard.departFrom || null;
    }

    public get visitPoint(): string | null {
        return this.wizard.visitPoint || null;
    }

    public get arriveAt(): Date | null {
        return this.wizard.arriveAt;
    }

    public get arriveTo(): string | null {
        return this.wizard.arriveTo || null;
    }

    public get customArea(): string | null {
        return this.wizard.customArea || null;
    }

    public async nextAsync(): Promise<void> {
        if (this._formRef.current) {
            await this._formRef.current.validateAsync();
        }

        this.saveContext();
        await super.nextAsync();
    }

    public get canNext(): boolean {
        return (!!this.wizard.area || !!this.wizard.customArea) &&
            (!!this.wizard.departBy && !!this.wizard.arriveAt) &&
            (!!this.wizard.departFrom && !!this.wizard.arriveTo);
    }

    private get minDepartByTime(): Date {
        return this.setMinTime();
    }

    private setMinTime(minTime: Date | null = null, offset: number = 0): Date {
        const selectedDate: Date = new Date(this.wizard.date);

        const isToday: boolean = (selectedDate == Utility.today());

        if (isToday) {
            return Utility.now();
        } else if (minTime) {
            selectedDate.setHours(minTime.getHours(), 0, 0, 0);
        } else {
            selectedDate.setHours(8, 0, 0, 0);
        }

        return selectedDate.addHours(offset);
    }

    private get visitingPointDurationVisible(): boolean {
        return (!!this.wizard.visitPoint) || (this.state.visitingPointDurationVisible);
    }

    private get minArriveAtTime(): Date {
        const visitingPointDurationOffset: number = WizardContext.getVisitingPointDurationTimeOffset(this.wizard);
        
        return this.setMinTime(this.departBy?.addHours(1), visitingPointDurationOffset);
    }

    private get maxDepartByTime(): Date {
        const date: Date = new Date(this.wizard.date);
        
        date.setHours(DEPART_BY_MAX_TIME, 59, 59, 999);
        
        return date;
    }

    private get maxArriveAtTime(): Date {
        const date: Date = new Date(this.wizard.date);
        
        date.setHours(ARRIVE_AT_MAX_TIME, 59, 59, 999);
        
        return date;
    }

    private visitingPointDurationItems(): SelectListItem[] {
        let durationItems: SelectListItem[] = EnumProvider.getVisitingPointDurationItems();
        
        const duration: number | null = WizardContext.getCruiseDuration(this.wizard);
        
        if (duration) {
            switch (duration) {
                case 1:
                    durationItems = durationItems.skipLast(4);
                    break;
                case 2: durationItems = durationItems.skipLast(3);
                    break;
                case 3: durationItems = durationItems.skipLast(2);
                    break;
                case 4: durationItems = durationItems.skipLast(1);
                    break;
                case 5: durationItems = durationItems.skipLast(0);
                    break;
            }
        } else if (this.wizard.departBy){
            const hours: number = this.wizard.departBy.getHours();
            
            switch (hours) {
                case DEPART_BY_MAX_TIME : durationItems = durationItems.skipLast(4); break;
                case 20 : durationItems = durationItems.skipLast(3); break;
                case 19 : durationItems = durationItems.skipLast(2); break;
                case 18 : durationItems = durationItems.skipLast(1); break;
            }
            
        }

        return durationItems;
    }

    private async setVisitingPointDurationVisible(value: boolean): Promise<void> {
        await this.setState({visitingPointDurationVisible: value});
    }

    private async onFocusVisitingPointAsync(): Promise<void> {
        await this.setVisitingPointDurationVisible(true);
    }

    private async onBlurVisitingPointAsync(): Promise<void> {
        if (!this.visitPoint) {
            this.wizard.visitingPointDuration = null;
            await this.setVisitingPointDurationVisible(false);
        }
    }

    public async initializeAsync(): Promise<void> {
        await super.initializeAsync();

        const areas: CruiseArea[] = await CruiseController.getAreasAsync(this.wizard.country?.code);

        if ((!this.wizard.area) && (!this.wizard.customArea) && (areas.length > 0)) {
            this.wizard.area = areas[0];
        }

        const customArea = new CruiseArea();
        customArea.name = Localizer.cruiseWizardCustomArea;
        customArea.id = CUSTOM_AREA_ID;
        areas.push(customArea);

        await this.setState({areas});
    }

    public renderButtons(): React.ReactNode {
        return (
            <React.Fragment>
                <Button block type={ButtonType.Dark} label={Localizer.cruiseWizardNext}
                        onClick={() => this.nextAsync()}/>

                <Button block type={ButtonType.Light} label={Localizer.cruiseWizardBack}
                        onClick={() => this.prevAsync()}/>
            </React.Fragment>
        )
    }

    public renderContent(): React.ReactNode {
        const initialized: boolean = (this.areas.length > 0);
        const single: boolean = (!this.isCustom) || (this.areas.length == 1);

        const departTime: string = (this.departBy != null)
            ? Localizer.cruiseOrdersDepartByTime.format(this.wizard.departBy)
            : Localizer.cruiseOrdersDepartBy;

        const arriveTime: string = (this.arriveAt != null)
            ? Localizer.cruiseOrdersArriveAtTime.format(this.wizard.arriveAt)
            : Localizer.cruiseOrdersArrivalAt;

        const durationStyle: any = (this.visitingPointDurationVisible) ? styles.extended : styles.hidden;

        return (
            <>
                {
                    (initialized) &&
                    (
                        <Form id={"form"}
                              ref={this._formRef}
                              className={this.css(styles.area, single && styles.single)}>

                            {
                                (this.areas.length > 1) &&
                                (
                                    <Dropdown required
                                              id={"areas"}
                                              className={styles.item}
                                              orderBy={DropdownOrderBy.None}
                                              items={this.areas}
                                              selectedItem={this.areaId}
                                              onChange={(sender, value) => this.setAreaAsync(value!)}
                                    />
                                )
                            }

                            {
                                (this.isCustom) &&
                                (
                                    <TextInput id={"area"}
                                               placeholder={Localizer.cruiseWizardOther}
                                               maxLength={CruiseConstants.keyLength}
                                               className={styles.item}
                                               value={this.customArea}
                                               onChange={(sender, value) => this.setCustomAreaAsync(value)}
                                    />
                                )
                            }

                            <div className={styles.route}>
                                <img
                                    className={this.css(styles.routeImage, !this.isMobile && styles.desktop)}
                                    alt={"route"}
                                    src={(this.isMobile) ? routeMobileImage : routeImage}/>
                            </div>

                            <TwoColumns>

                                <TextInput required clearButton
                                           id={"departFrom"}
                                           placeholder={Localizer.cruiseOrdersDepartFrom}
                                           maxLength={CruiseConstants.hashLength}
                                           value={this.departFrom}
                                           onChange={(sender, value) => this.setDepartFromAsync(value)}
                                />

                                    <DateInput required showTime showOnlyTime
                                               id={"departBy"}
                                               timeIntervals={60}
                                               className={styles.timeInput}
                                               ref={this._departByTimeInputRef}
                                               minDate={Utility.now()}
                                               minTime={this.minDepartByTime}
                                               maxTime={this.maxDepartByTime}

                                               // The 'placeholder' and 'noValidate' applied due to need to show the text with the selected value inside the input. 
                                               // value={this.departBy}
                                               placeholder={departTime}
                                               noValidate={(this.departBy != null)}
                                               onChange={(value: Date) => this.setDepartByAsync(value)}
                                    />

                            </TwoColumns>

                            <TextInput clearButton
                                       id={"visitingPoint"}
                                       placeholder={Localizer.cruiseOrdersAddVisitPoint}
                                       maxLength={CruiseConstants.hashLength}
                                       value={this.visitPoint}
                                       onFocus={() => this.onFocusVisitingPointAsync()}
                                       onBlur={() => this.onBlurVisitingPointAsync()}
                                       onChange={(sender, value) => this.setVisitPointAsync(value)}
                            />

                            <Dropdown id={"visitingPointDuration"}
                                      className={this.css(styles.visitingPointDuration, durationStyle)}
                                      requiredType={DropdownRequiredType.Manual}
                                      orderBy={DropdownOrderBy.None}
                                      nothingSelectedText={Localizer.cruiseOrdersHowLongTake}
                                      items={this.visitingPointDurationItems()}
                                      selectedItem={this.wizard.visitingPointDuration}
                                      onChange={(sender, value) => this.setVisitingPointDuration(value!)}
                            />

                            <TwoColumns>

                                <TextInput required clearButton
                                           id={"arriveTo"}
                                           placeholder={Localizer.cruiseOrdersArriveTo}
                                           maxLength={CruiseConstants.hashLength}
                                           value={this.arriveTo}
                                           onChange={(sender: TextInput, value: string) => this.setArriveToAsync(value)}
                                />

                                    <DateInput required showTime showOnlyTime
                                               id={"arriveAt"}
                                               className={styles.timeInput}
                                               timeIntervals={60}
                                               ref={this._arriveAtTimeInputRef}
                                               minDate={Utility.now()}
                                               minTime={this.minArriveAtTime}
                                               maxTime={this.maxArriveAtTime}

                                               // The 'placeholder' and 'noValidate' applied due to need to show the text with the selected value inside the input.
                                               // value={this.arriveAt}
                                               placeholder={arriveTime}
                                               noValidate={(this.arriveAt != null)}
                                               onChange={(value: Date) => this.setArriveAtAsync(value)}
                                    />

                            </TwoColumns>

                        </Form>
                    )
                }
            </>
        );
    }
}